import React from 'react';
import { Cookie } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import SoldOutBadge from '../ui/SoldOutBadge';

const SkeletonCard = () => (
  <div className="rounded-lg shadow-md overflow-hidden animate-pulse">
    <div className="aspect-square bg-gray-200" />
    <div className="p-4 space-y-2">
      <div className="h-4 bg-gray-200 rounded w-3/4 mx-auto" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-9 bg-gray-200 rounded w-full mt-4" />
    </div>
  </div>
);

const GalletasSection = ({
  galletas,
  galletasEspeciales,
  packs,
  openLightbox,
  openProductDetail,
  handleOrderClick,
  getImageUrl,
  loadingProducts
}) => {
  return (
    <section id="galletas" className="py-20 px-6 bg-white">
      <div className="container mx-auto">
        <div className="text-center mb-12">
          <Cookie className="w-16 h-16 mx-auto mb-4 text-caramel" />
          <h2 className="text-4xl md:text-5xl font-bold text-chocolate mb-4">Nuestras Galletas Artesanales</h2>
          <p className="text-xl text-chocolate-light max-w-2xl mx-auto">
            Galletas frescas hechas en casa. ¡Escoge tus sabores favoritos!
          </p>
          <p className="text-sm text-amber-700 bg-amber-50 inline-block px-4 py-2 rounded-full mt-4">
            ⚠️ Los sabores están sujetos a disponibilidad
          </p>
        </div>

        {/* Galletas Individuales */}
        <div className="mb-12">
          <h3 className="text-2xl font-bold text-chocolate text-center mb-2">Sabores Disponibles</h3>
          <p className="text-center text-chocolate-light mb-6">$1.50 por unidad</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {loadingProducts && galletas.length === 0
              ? Array.from({ length: 4 }).map((_, i) => <SkeletonCard key={i} />)
              : galletas.map((galleta) => (
              <Card key={galleta.id} className={`border-0 shadow-md hover:shadow-lg transition-all duration-300 overflow-hidden group h-full ${galleta.available === false ? 'opacity-75' : ''}`}>
                <div className="flex flex-col h-full">
                  <div
                    className="aspect-square overflow-hidden cursor-pointer relative"
                    onClick={() => openLightbox(getImageUrl(galleta.image), galleta.name)}
                  >
                    <img
                      src={getImageUrl(galleta.image)}
                      alt={`Galleta ${galleta.name} – Brazolovers, Quito`}
                      loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {galleta.available === false && <SoldOutBadge />}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <span className="bg-white/90 text-chocolate px-2 py-1 rounded-full text-xs font-medium">
                        🔍
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-4 flex flex-col flex-grow">
                    <div className="flex-grow space-y-2">
                      <h4 className="font-bold text-chocolate text-base text-center">{galleta.name}</h4>
                      <p className="text-sm text-chocolate-light text-center line-clamp-2">{galleta.description}</p>
                    </div>
                    {galleta.available === false ? (
                      <Button disabled className="w-full bg-gray-300 text-gray-600 mt-4 min-h-[44px] cursor-not-allowed hover:bg-gray-300" size="sm">
                        Agotado
                      </Button>
                    ) : (
                      <Button
                        onClick={() => openProductDetail(galleta, 'galleta')}
                        className="w-full bg-[#d4a574] hover:bg-[#c49564] text-white mt-4 min-h-[44px] transition-colors duration-200"
                        size="sm"
                      >
                        Ver Detalles
                      </Button>
                    )}
                  </CardContent>
                </div>
              </Card>
            ))}
          </div>
        </div>


        {/* Packs */}
        {packs.length > 0 && (
          <div id="galletas-packs">
            <h3 className="text-2xl font-bold text-chocolate text-center mb-6">Packs de Galletas</h3>
            <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
              {[...packs].sort((a, b) => {
                if (a.packType === 'custom') return 1;
                if (b.packType === 'custom') return -1;
                return (a.quantity || 0) - (b.quantity || 0);
              }).map((pack) => (
                <Card key={pack.id} className={`border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group flex flex-col ${pack.available === false ? 'opacity-75' : ''}`}>
                  <div
                    className="aspect-square overflow-hidden relative cursor-pointer"
                    onClick={() => openLightbox(pack.image, pack.name)}
                  >
                    <img
                      src={pack.image}
                      alt={`${pack.name} – Brazolovers, Quito`}
                      loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {pack.available === false && <SoldOutBadge />}
                    {pack.packType === 'fixed' && (
                      <div className="absolute top-3 right-3 bg-[#d4a574] text-white px-3 py-1 rounded-full font-bold text-sm shadow-lg">
                        x{pack.quantity}
                      </div>
                    )}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <span className="bg-white/90 text-chocolate px-3 py-1 rounded-full text-sm font-medium">
                        🔍 Ver imagen
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6 flex flex-col flex-1">
                    <div className="flex-grow space-y-3">
                      <h3 className="text-xl font-bold text-chocolate text-center">{pack.name}</h3>
                      <p className="text-sm text-chocolate-light text-center">{pack.description}</p>
                      {pack.packType === 'custom' ? (
                        <div className="text-center space-y-1">
                          <p className="text-xs text-chocolate-light">Base (≤12): $14.00</p>
                          <p className="text-xs text-caramel">13-48: $1.17 c/u | 49+: $1.10 c/u</p>
                        </div>
                      ) : (
                        <div className="text-center">
                          <span className="text-2xl font-bold text-caramel">${pack.price.toFixed(2)}</span>
                        </div>
                      )}
                    </div>
                    {pack.available === false ? (
                      <Button disabled className="w-full bg-gray-300 text-gray-600 mt-4 cursor-not-allowed hover:bg-gray-300">
                        Agotado
                      </Button>
                    ) : (
                      <Button
                        onClick={() => handleOrderClick(pack, 'pack')}
                        className="w-full bg-caramel hover:bg-amber-600 text-white mt-4"
                      >
                        {pack.packType === 'custom' ? 'Personalizar' : 'Seleccionar Sabores'}
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
            <p className="text-center text-sm text-amber-700 bg-amber-50 inline-block px-4 py-2 rounded-full mt-6 mx-auto block w-fit">
              ⚠️ Sabores sujeto a disponibilidad
            </p>
          </div>
        )}

        {/* Galletas Especiales */}
        {galletasEspeciales.length > 0 && (
          <div className="mt-16">
            <h3 className="text-2xl font-bold text-chocolate text-center mb-6">✨ Galletas Especiales</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 max-w-5xl mx-auto">
              {galletasEspeciales.map((item) => (
                <Card key={item.id} className={`border-0 shadow-lg hover:shadow-2xl transition-all duration-300 overflow-hidden group ${item.available === false ? 'opacity-75' : ''}`}>
                  <div
                    className="aspect-square overflow-hidden cursor-pointer relative"
                    onClick={() => openLightbox(getImageUrl(item.image), item.name)}
                  >
                    <img
                      src={getImageUrl(item.image)}
                      alt={`${item.name} – Brazolovers, Quito`}
                      loading="lazy" decoding="async" className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    {item.available === false && <SoldOutBadge />}
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center opacity-0 group-hover:opacity-100">
                      <span className="bg-white/90 text-chocolate px-3 py-1 rounded-full text-sm font-medium">
                        🔍 Ver imagen
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-4 space-y-3">
                    <h4 className="text-base font-bold text-chocolate text-center">{item.name}</h4>
                    {item.description && (
                      <p className="text-xs text-chocolate-light text-center line-clamp-2">{item.description}</p>
                    )}
                    <div className="text-center">
                      <span className="text-xl font-bold text-caramel">${item.price.toFixed(2)}</span>
                    </div>
                    {item.available === false ? (
                      <Button disabled className="w-full bg-gray-300 text-gray-600 cursor-not-allowed hover:bg-gray-300">
                        Agotado
                      </Button>
                    ) : (
                      <Button
                        onClick={() => openProductDetail(item, 'galleta_especial')}
                        className="w-full bg-[#d4a574] hover:bg-[#c49564] text-white transition-colors duration-200"
                      >
                        Ver Detalles
                      </Button>
                    )}
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default GalletasSection;
